import { cur } from '@/lib/currency';
import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, DollarSign, Percent, FileText, RefreshCw, ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevRight, BarChart3, HelpCircle, AlertTriangle, Trash2 } from 'lucide-react';
import api from '@/lib/api';
import { exportPnLToPDF, exportBranchPnLToPDF } from '@/lib/pdfExport';
import { ExportButtons } from '@/components/ExportButtons';
import { FeatureTour, useTourReplay } from '@/components/FeatureTour';
import { useLedgerDrilldown } from '@/components/accounting/LedgerDrilldown';
import { ReportNotifyButton } from '@/components/accounting/ReportNotifyButton';
import { BranchMultiSelect } from '@/components/BranchMultiSelect';
import { GlAccountingStrip } from '@/components/accounting/GlAccountingStrip';
import { PnlCompleteness, PnlReconciliation, PnlCashCreditSection } from '@/components/accounting/PnlBridgeSections';
import { VatModeToggle } from '@/components/common/VatModeToggle';
import { useVatModeStore } from '@/stores/vatModeStore';
import { FreshnessTag } from '@/components/common/FreshnessTag';
import { SectionBar, pageLink, amountCols } from '@/components/accounting/SectionBar';

const fmt = (v) => (v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtSR = (v) => `${cur()} ${fmt(v)}`;
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const getMonthRange = (y, m) => {
  const s = `${y}-${String(m+1).padStart(2,'0')}-01`;
  const e = `${y}-${String(m+1).padStart(2,'0')}-${new Date(y, m+1, 0).getDate()}`;
  return { start: s, end: e };
};

export default function ProfitLossPage() {
  const now = new Date();
  const [data, setData] = useState(null);
  const [branchData, setBranchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  // iter — multi-branch filter: array of selected branch ids ([] = all branches).
  const [branchFilter, setBranchFilter] = useState([]);
  const [mode, setMode] = useState('month');
  const [selYear, setSelYear] = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth());
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [view, setView] = useState('summary');
  const [showBalances, setShowBalances] = useState(false);
  // iter816 — Duplicate-expense finder (fixes "Visa Fee / id card counted twice").
  const [dupOpen, setDupOpen] = useState(false);
  const [dupData, setDupData] = useState(null);
  const [dupLoading, setDupLoading] = useState(false);

  useEffect(() => { api.get('/branches').then(r => setBranches(r.data || [])).catch(() => {}); }, []);

  const [consolidate, setConsolidate] = useState(false);

  const fetchPnL = useCallback(async () => {
    setLoading(true);
    try {
      let sd, ed;
      if (mode === 'month') { const r = getMonthRange(selYear, selMonth); sd = r.start; ed = r.end; }
      else { sd = startDate; ed = endDate; }
      const params = {};
      if (sd) params.start_date = sd;
      if (ed) params.end_date = ed;
      // iter — multi-branch filter: send branch_id for exactly 1 selection,
      // branch_ids (CSV) for 2+, nothing for 0 (all branches).
      if (branchFilter.length === 1) params.branch_id = branchFilter[0];
      else if (branchFilter.length > 1) params.branch_ids = branchFilter.join(',');
      if (view === 'branches') {
        // Judgment call: the page-level branch filter also narrows the "By
        // Branch" breakdown (rather than always showing every branch) so the
        // filter behaves consistently across both view modes on this page.
        const res = await api.get('/accounting/profit-loss/by-branch', { params });
        setBranchData(res.data); setData(null);
      } else {
        // Consolidate (use base_amount) only valid when no branch filter at all
        if (branchFilter.length === 0 && consolidate) params.consolidate = true;
        const res = await api.get('/accounting/profit-loss', { params });
        setData(res.data); setBranchData(null);
      }
    } catch { toast.error('Failed to load P&L'); }
    setLoading(false);
  }, [mode, selYear, selMonth, startDate, endDate, view, branchFilter, consolidate]);

  useEffect(() => { fetchPnL(); }, [fetchPnL]);

  const prevMonth = () => { if (selMonth === 0) { setSelMonth(11); setSelYear(y => y-1); } else setSelMonth(m => m-1); };
  const nextMonth = () => { if (selMonth === 11) { setSelMonth(0); setSelYear(y => y+1); } else setSelMonth(m => m+1); };
  const periodLabel = mode === 'month' ? `${MONTHS[selMonth]} ${selYear}` : `${startDate || '...'} to ${endDate || '...'}`;
  // iter816 — Effective date range for the server-side export. In MONTH mode
  // startDate/endDate state are empty (they only hold the custom-range values),
  // so the export was silently sending no dates and dumping ALL-time data. Use
  // the same month range the on-screen report uses so PDF/Excel match exactly.
  const effRange = mode === 'month' ? getMonthRange(selYear, selMonth) : { start: startDate, end: endDate };

  const checkDuplicates = async () => {
    setDupOpen(true); setDupLoading(true); setDupData(null);
    try {
      const params = {};
      if (effRange.start) params.start_date = effRange.start;
      if (effRange.end) params.end_date = effRange.end;
      // /expenses/find-duplicates is out of scope for this change and only
      // supports a single branch_id, so only send one when exactly 1 branch
      // is selected (matches prior single-branch behavior for 0/1 selections).
      if (branchFilter.length === 1) params.branch_id = branchFilter[0];
      const r = await api.get('/expenses/find-duplicates', { params });
      setDupData(r.data);
    } catch { toast.error('Failed to scan for duplicates'); }
    setDupLoading(false);
  };

  const deleteDupRow = async (id) => {
    try {
      const _r = await api.delete(`/expenses/${id}`);
      toast.success(_r.data?.message || 'Duplicate expense deleted');
      await Promise.all([checkDuplicates(), fetchPnL()]);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const { replay: replayTour, forceStart: forceTour, replayKey } = useTourReplay('pnl-v1');
  const tourSteps = [
    { selector: '[data-testid="profit-loss-page"] h1', title: 'Profit & Loss', content: 'Your revenue, cost of sales, gross profit, and net profit — all for the period you choose.', placement: 'bottom' },
    { selector: '[data-tour="pnl-filters"]', title: 'Pick Month or Range', content: 'Switch between a single month or a custom date range. Apply a branch filter to zero-in on one or more locations.', placement: 'bottom' },
    { selector: '[data-testid="view-branches"]', title: 'Branch-wise View', content: 'Click here to compare all branches side by side, with expandable cards for each.', placement: 'bottom' },
    { selector: '[data-testid="export-pnl-pdf"]', title: 'Clean PDF Export', content: 'Export a polished, data-driven PDF — no more messy screenshots.', placement: 'left' },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-5 max-w-6xl mx-auto" data-testid="profit-loss-page-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-200/50"><TrendingUp size={24} className="text-white" /></div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">Profit & Loss</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {periodLabel} <FreshnessTag freshness={data?.freshness} testId="pnl-freshness" />
                {data?.display_currency && (
                  <span className="ml-2 inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-muted text-foreground font-mono">
                    {data.display_currency}
                    {data.conversion_applied && <span className="text-emerald-600">· consolidated via FX</span>}
                    {!data.conversion_applied && branchFilter.length === 1 && <span className="text-info">· native branch currency</span>}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={replayTour}
              data-testid="tour-replay-btn"
              title="Replay tour"
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-info bg-info/10 border border-info/30 rounded-lg hover:bg-info/20"
            >
              <HelpCircle size={14} /> Tour
            </button>
            <label className="flex items-center gap-1.5 cursor-pointer text-xs text-muted-foreground bg-white border border-border rounded-lg px-2.5 py-1.5 hover:bg-muted/50">
              <input type="checkbox" checked={showBalances} onChange={e => setShowBalances(e.target.checked)} className="w-3.5 h-3.5 rounded" />
              Opening/Closing
            </label>
            <button
              onClick={checkDuplicates}
              data-testid="pnl-check-duplicates-btn"
              title="Scan this period for duplicate expenses (same day + amount + branch)"
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-300 rounded-lg hover:bg-amber-100"
            >
              <AlertTriangle size={14} /> Duplicates
            </button>
            <ExportPnLBtn view={view} data={data} branchData={branchData} periodLabel={periodLabel} />
            <ReportNotifyButton report="pnl" label="Notify" />
            <ExportButtons dataType="profit_loss" branchIds={branchFilter} startDate={effRange.start} endDate={effRange.end} testid="pnl-export" />
          </div>
        </div>
        {(() => { const r = mode === 'month' ? getMonthRange(selYear, selMonth) : { start: startDate, end: endDate }; return (
          <GlAccountingStrip startDate={r.start} endDate={r.end} branchIds={branchFilter} operational={data?.operational?.net} operationalLabel="Operational net profit" />
        ); })()}

        {/* Filters */}
        <Card data-tour="pnl-filters"><CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="flex bg-muted rounded-lg p-0.5">
              <button onClick={() => setMode('month')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${mode === 'month' ? 'bg-white shadow text-foreground' : 'text-muted-foreground'}`} data-testid="mode-month">Month</button>
              <button onClick={() => setMode('range')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${mode === 'range' ? 'bg-white shadow text-foreground' : 'text-muted-foreground'}`} data-testid="mode-range">Date Range</button>
            </div>
            {mode === 'month' ? (
              <div className="flex items-center gap-1" data-testid="month-picker">
                <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-muted"><ChevronLeft size={16} /></button>
                <Select value={String(selMonth)} onValueChange={v => setSelMonth(parseInt(v))}><SelectTrigger className="w-[100px] h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent>{MONTHS.map((m,i) => <SelectItem key={i} value={String(i)}>{m}</SelectItem>)}</SelectContent></Select>
                <Select value={String(selYear)} onValueChange={v => setSelYear(parseInt(v))}><SelectTrigger className="w-[80px] h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent>{Array.from({length:5},(_,i) => now.getFullYear()-2+i).map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select>
                <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-muted"><ChevronRight size={16} /></button>
              </div>
            ) : (
              <div className="flex items-center gap-2"><Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-36 h-8 text-xs" /><span className="text-muted-foreground text-xs">to</span><Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-36 h-8 text-xs" /></div>
            )}
            <VatModeToggle />
            <div className="h-5 w-px bg-muted hidden sm:block" />
            <div className="flex bg-muted rounded-lg p-0.5">
              <button onClick={() => setView('summary')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${view === 'summary' ? 'bg-white shadow text-foreground' : 'text-muted-foreground'}`} data-testid="view-summary">Summary</button>
              <button onClick={() => setView('branches')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${view === 'branches' ? 'bg-white shadow text-foreground' : 'text-muted-foreground'}`} data-testid="view-branches-2">By Branch</button>
            </div>
            {view === 'summary' && branches.length > 0 && (
              <>
              <BranchMultiSelect value={branchFilter} onChange={setBranchFilter} branches={branches} width="w-40" testid="pnl-branch-filter" />
              {branchFilter.length === 0 && (
                <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none px-2 py-1 rounded border border-border hover:bg-muted/40"
                  title="Consolidate multi-currency branches into tenant base currency using stored FX rates">
                  <input type="checkbox" checked={consolidate}
                    onChange={(e) => setConsolidate(e.target.checked)}
                    data-testid="consolidate-toggle"
                    className="rounded border-border" />
                  <span>Consolidate ({data?.display_currency || 'base'})</span>
                </label>
              )}
              </>
            )}
            {mode === 'range' && <Button size="sm" onClick={fetchPnL} className="h-8 text-xs ml-auto"><RefreshCw size={14} className="mr-1" />Apply</Button>}
          </div>
        </CardContent></Card>

        {loading ? <div className="flex items-center justify-center min-h-[30vh]"><div className="w-8 h-8 border-4 border-success border-t-transparent rounded-full animate-spin" /></div>
         : view === 'branches' && branchData ? <BranchView data={branchData} showBalances={showBalances} />
         : data ? <SummaryView data={data} showBalances={showBalances} branchId={branchFilter.length === 1 ? branchFilter[0] : null} onSettled={fetchPnL} />
         : null}
      </div>
      <FeatureTour key={replayKey} tourId="pnl-v1" steps={tourSteps} forceStart={forceTour} />

      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="max-w-2xl max-h-[80dvh] overflow-y-auto" data-testid="pnl-duplicates-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle size={18} className="text-amber-600" /> Duplicate Expenses — {periodLabel}</DialogTitle>
            <DialogDescription>
              Expenses with the same day, amount and branch (even under different categories) — a likely double entry that inflates your reports. Review and delete the extra one.
            </DialogDescription>
          </DialogHeader>
          {dupLoading ? (
            <div className="py-10 text-center text-sm text-muted-foreground">Scanning…</div>
          ) : !dupData || dupData.group_count === 0 ? (
            <div className="py-10 text-center text-sm text-emerald-600 font-medium" data-testid="pnl-duplicates-none">No duplicate expenses found in this period. ✓</div>
          ) : (
            <div className="space-y-3" data-testid="pnl-duplicates-list">
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Found <b>{dupData.group_count}</b> duplicate cluster{dupData.group_count > 1 ? 's' : ''} · <b>{dupData.total_extra_rows}</b> extra row{dupData.total_extra_rows > 1 ? 's' : ''} inflating expenses by <b>{fmtSR(dupData.total_inflated_amount)}</b>.
              </div>
              {dupData.duplicate_groups.map((g, gi) => (
                <div key={gi} className="border border-border rounded-lg p-3" data-testid={`dup-group-${gi}`}>
                  <div className="text-xs font-semibold mb-2">{g.date} · {fmtSR(g.amount)} · {g.branch_name} · {g.count}× ({g.distinct_categories.join(', ')})</div>
                  <div className="space-y-1.5">
                    {g.rows.map((r, ri) => (
                      <div key={r.id} className="flex items-center gap-2 text-xs border-t border-border/40 pt-1.5" data-testid={`dup-row-${r.id}`}>
                        <span className="font-medium">{r.category}</span>
                        <span className="text-muted-foreground truncate max-w-[220px]">{r.description || r.supplier_name || r.employee_name || '—'}</span>
                        <span className="text-muted-foreground">{r.payment_mode}</span>
                        {ri === 0
                          ? <span className="ml-auto text-[10px] text-emerald-600 font-medium">keep</span>
                          : <Button size="sm" variant="outline" className="ml-auto h-6 text-[10px] px-2 text-error border-error/40 hover:bg-error/10" onClick={() => deleteDupRow(r.id)} data-testid={`dup-delete-${r.id}`}><Trash2 size={12} className="mr-1" />Delete</Button>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function PctBadge({ value, color = '' }) {
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${color || 'bg-muted text-muted-foreground'}`}>{value == null ? 'n/a' : `${value}%`}</span>;
}
function EmailPnLBtn() {
  const [open, setOpen] = useState(false);
  const [recipients, setRecipients] = useState('');
  const [cadence, setCadence] = useState('off');
  const [lastSent, setLastSent] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/pnl-notifications/settings');
      setRecipients((data.recipients || []).join(', '));
      setCadence(data.cadence || 'off');
      setLastSent(data.last_sent);
    } catch { /* admin only */ }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const parseList = () => recipients.split(',').map((e) => e.trim()).filter(Boolean);

  const save = async () => {
    setBusy(true);
    try {
      await api.post('/pnl-notifications/settings', { recipients: parseList(), cadence });
      toast.success('P&L delivery settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  const sendNow = async () => {
    const list = parseList();
    if (!list.length) { toast.error('Add at least one recipient email'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/pnl-notifications/send-now', { recipients: list });
      if (data.sent) { toast.success(`P&L sent to ${list.length} recipient(s)`); setLastSent(data.at); }
      else toast.error('Email failed to send');
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="pnl-email-open"
        className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-muted-foreground bg-white border border-border rounded-lg hover:bg-muted/50"
      >
        <FileText size={14} />Email P&amp;L
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="pnl-email-dialog">
          <DialogHeader>
            <DialogTitle>Email Profit &amp; Loss · تقرير الأرباح والخسائر</DialogTitle>
            <DialogDescription>Send this month's P&amp;L now, or schedule monthly delivery. Subjects are bilingual (EN/AR).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase block mb-1">Recipients (comma-separated)</label>
              <Input value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="owner@company.com, accountant@company.com" data-testid="pnl-email-recipients" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase block mb-1">Schedule</label>
              <Select value={cadence} onValueChange={setCadence}>
                <SelectTrigger data-testid="pnl-email-cadence"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off (manual only)</SelectItem>
                  <SelectItem value="monthly">Monthly (1st @ 07:00 UTC)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {lastSent && <p className="text-[11px] text-muted-foreground">Last sent: {new Date(lastSent).toLocaleString()}</p>}
          </div>
          <div className="flex justify-between gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={save} disabled={busy}>Save schedule</Button>
            <Button size="sm" onClick={sendNow} disabled={busy} data-testid="pnl-email-send-now">{busy ? 'Sending…' : 'Send Now'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}



function ExportPnLBtn({ view, data, branchData, periodLabel }) {
  const handleExport = () => {
    if (view === 'branches') {
      if (!branchData) return;
      exportBranchPnLToPDF(branchData, 'Profit & Loss by Branch', periodLabel);
    } else {
      if (!data) return;
      exportPnLToPDF(data, 'Profit & Loss Statement', periodLabel, { inclVat: useVatModeStore.getState().inclVat });
    }
  };
  return (
    <button
      onClick={handleExport}
      data-testid="export-pnl-pdf-2"
      className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-muted-foreground bg-white border border-border rounded-lg hover:bg-muted/50"
    >
      <FileText size={14} />Export PDF
    </button>
  );
}

function LineItem({ label, amount, pct, indent = 0, bold = false, color = 'text-foreground', bg = '', drillAccount, onDrill }) {
  const clickable = drillAccount && onDrill;
  return (
    <div className={`flex items-center justify-between px-5 py-2.5 border-b border-stone-50 hover:bg-muted/50 ${bg}`} style={{ paddingLeft: `${20 + indent * 16}px` }}>
      {clickable ? (
        <button
          type="button"
          onClick={() => onDrill(drillAccount, label)}
          className={`${bold ? 'font-semibold' : ''} ${color} text-sm text-left hover:underline decoration-dotted underline-offset-2`}
          data-testid={`pnl-drill-${drillAccount}`}
        >{label}</button>
      ) : (
        <span className={`${bold ? 'font-semibold' : ''} ${color} text-sm`}>{label}</span>
      )}
      <div className="flex items-center gap-3">
        {pct !== undefined && <PctBadge value={pct} />}
        <span className={`${bold ? 'font-bold' : 'font-medium'} ${color} text-sm w-32 text-right`}>{fmtSR(amount)}</span>
      </div>
    </div>
  );
}

function SectionHeader({ label, amount, pct, color, bgClass, actions }) {
  return (
    <div className={`px-5 py-3 flex items-center justify-between gap-2 ${bgClass}`}>
      <span className={`font-semibold ${color} text-sm flex items-center gap-2 flex-wrap`}>{label}{actions}</span>
      <div className="flex items-center gap-3">
        {pct !== undefined && <PctBadge value={pct} color={bgClass.replace('bg-', 'bg-').replace('/50', '/80').replace('/70', '')} />}
        <span className={`font-bold ${color} w-32 text-right`}>{fmtSR(amount)}</span>
      </div>
    </div>
  );
}

function SummaryView({ data, showBalances, branchId, onSettled }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (g) => setExpanded(p => ({ ...p, [g]: !p[g] }));
  const rev = data.revenue?.total || 1;
  const pct = (v) => ((v || 0) / rev * 100).toFixed(1);
  const inclVat = useVatModeStore((s) => s.inclVat);
  const salesShown = inclVat ? data.revenue?.gross_incl_vat : data.revenue?.total;

  // Drill into an account's source transactions, scoped to the P&L period.
  // iter823 — also pass branchId so the drill-down mirrors the branch
  // filter currently selected on the P&L page.
  const { openLedger } = useLedgerDrilldown();
  const per = { start: data?.period?.start, end: data?.period?.end, branchId: branchId || null };
  const sec = (title, rows) => ({ title: `P&L · ${title}`, subtitle: `${per.start} to ${per.end}${branchId ? ' · selected branch' : ' · all permitted branches'}`,
    filename: `pnl_${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30)}_${branchId ? 'selected-branch' : 'all-branches'}_${per.start}_${per.end}`, cols: amountCols, rows });
  const drillAccount = (account, label) => {
    if (!account) return;
    openLedger(account, {
      name: label || account,
      startDate: data?.period?.start,
      endDate: data?.period?.end,
      branchId: branchId || null,
    });
  };

  // Inter-branch "Settle now"
  const [settleTarget, setSettleTarget] = useState(null);
  const [settleMode, setSettleMode] = useState('cash');
  const [settleAmount, setSettleAmount] = useState('');
  const [settling, setSettling] = useState(false);
  const openSettle = (target) => {
    setSettleTarget(target);
    setSettleAmount(String(target.amount ?? ''));
    setSettleMode('cash');
  };
  const doSettle = async () => {
    if (!settleTarget) return;
    const amt = parseFloat(settleAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setSettling(true);
    try {
      await api.post('/reports/inter-branch/settle', {
        from_branch_id: settleTarget.from_branch_id,
        to_branch_id: settleTarget.to_branch_id,
        amount: amt,
        payment_mode: settleMode,
      });
      toast.success('Inter-branch balance settled');
      setSettleTarget(null);
      onSettled && onSettled();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to settle');
    } finally { setSettling(false); }
  };

  return (
    <div id="pnl-content" className="space-y-5">
      {/* Headline: Net Sales − COGS = Gross Profit − Operating Expenses = Net Profit (posted GL, excl. VAT) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="pnl-headline">
        {[
          { k: 'sales', label: 'Net sales', val: data.revenue?.total, pct: '100', color: 'emerald', icon: TrendingUp, sub: `VAT ${fmtSR(data.revenue?.output_vat_gl)} excluded` },
          { k: 'cogs', label: '− Cost of goods', val: data.cost_of_sales?.total, pct: pct(data.cost_of_sales?.total), color: 'amber', icon: DollarSign, sub: 'purchase-based' },
          { k: 'gross', label: '= Gross profit', val: data.gross_profit, pct: data.gross_margin, color: 'blue', icon: Percent },
          { k: 'opex', label: '− Operating expenses', val: data.operating_expenses?.total, pct: pct(data.operating_expenses?.total), color: 'rose', icon: TrendingDown },
          { k: 'net', label: data.net_profit >= 0 ? '= Net profit' : '= Net loss', val: data.net_profit, pct: data.net_margin, color: data.net_profit >= 0 ? 'emerald' : 'red', icon: data.net_profit >= 0 ? TrendingUp : TrendingDown,
            sub: data.operational ? `Daily records: ${fmtSR(data.operational.net)}` : '' },
        ].map((m, i) => (
          <Card key={m.k} className={`border-${m.color}-200 ${i === 4 ? 'border-2' : ''}`} data-testid={`pnl-card-${m.k}`}>
            <CardContent className="p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <m.icon size={14} className={`text-${m.color}-600`} />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{m.label}</span>
                <PctBadge value={m.pct} />
              </div>
              <p className={`text-lg font-bold text-${m.color}-700 tabular-nums`}>{fmtSR(m.val)}</p>
              {m.sub && <p className="text-[10px] text-muted-foreground mt-0.5">{m.sub}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Opening Balance */}
      {showBalances && (
        <Card className="border-border bg-muted/50">
          <CardContent className="p-4 flex justify-between items-center">
            <span className="text-sm font-medium text-muted-foreground">Opening Balance (Retained Earnings)</span>
            <span className="font-bold text-foreground">{fmtSR(0)}</span>
          </CardContent>
        </Card>
      )}

      {/* P&L Statement */}
      <Card><CardContent className="p-0">
        <SectionHeader label={`Revenue (${inclVat ? 'Incl. VAT' : 'Excl. VAT'})`} amount={salesShown} pct="100" color="text-success" bgClass="bg-success/10"
          actions={<SectionBar testId="pnl-sec-revenue" detailsTo={pageLink('/sales', per)} spec={() => sec('Revenue', [
            { item: 'Net sales (posted GL, excl. VAT)', value: data.revenue?.total }, { item: 'VAT collected (posted GL)', value: data.revenue?.output_vat_gl },
            { item: 'Gross sales in daily records (incl. VAT)', value: data.revenue?.sales_gross }])} />} />
        <LineItem label="Net sales (posted GL, excl. VAT)" amount={data.revenue?.total} pct={pct(data.revenue?.total)} indent={1} />
        <LineItem label="VAT collected (posted GL)" amount={data.revenue?.output_vat_gl} indent={1} color="text-muted-foreground" />
        <LineItem label="Gross sales in daily records (incl. VAT) — see Branch Summary for payment split" amount={data.revenue?.sales_gross} indent={1} color="text-muted-foreground" />
        {inclVat && <LineItem label="Input VAT on purchases & expenses (not in costs below)" amount={data.input_vat_gl} indent={1} color="text-muted-foreground" />}

        <div className="border-t border-border" />
        <SectionHeader label="Cost of Sales (Purchases)" amount={data.cost_of_sales?.total} pct={pct(data.cost_of_sales?.total)} color="text-warning" bgClass="bg-warning/10"
          actions={<SectionBar testId="pnl-sec-cogs" detailsTo={pageLink('/expenses', per)} spec={() => sec('Cost of goods (purchase-based, posted GL)', [
            ...(data.cost_of_sales?.items || []).map((i) => ({ item: `${i.account || ''} ${i.name || ''}`.trim(), value: i.total })), { item: 'TOTAL', value: data.cost_of_sales?.total }])} />} />
        {(data.cost_of_sales?.items && data.cost_of_sales.items.length > 0)
          ? data.cost_of_sales.items.map((item, idx) => (
              <LineItem key={idx} label={item.name || 'Supplier Purchases'} amount={item.total} pct={pct(item.total)} indent={1} drillAccount={item.account} onDrill={drillAccount} />
            ))
          : (data.cost_of_sales?.total > 0 && (
              <LineItem label="Supplier Purchases" amount={data.cost_of_sales?.total} pct={pct(data.cost_of_sales?.total)} indent={1} />
            ))
        }

        <SectionHeader label="Gross Profit" amount={data.gross_profit} pct={data.gross_margin} color="text-info" bgClass="bg-info/10 border-t border-border" />

        <SectionHeader label="Operating Expenses" amount={data.operating_expenses?.total} pct={pct(data.operating_expenses?.total)} color="text-error" bgClass="bg-error/10 border-t border-border"
          actions={<SectionBar testId="pnl-sec-opex" detailsTo={pageLink('/expenses', per)} spec={() => sec('Operating expenses (posted GL)', [
            ...Object.entries(data.operating_expenses?.by_group || {}).flatMap(([g, info]) => (info.items || []).map((i) => ({ item: `${g} · ${i.account || ''} ${i.name || ''}`.trim(), value: i.total }))),
            { item: 'TOTAL', value: data.operating_expenses?.total }])} />} />
        {Object.entries(data.operating_expenses?.by_group || {}).map(([group, info]) => (
          <div key={group}>
            <button onClick={() => toggle(group)} className="w-full flex items-center justify-between px-5 py-2.5 border-b border-stone-50 hover:bg-muted/50">
              <div className="flex items-center gap-2" style={{ paddingLeft: '8px' }}>
                {expanded[group] ? <ChevronDown size={14} className="text-muted-foreground" /> : <ChevRight size={14} className="text-muted-foreground" />}
                <span className="text-foreground font-medium text-sm">{group}</span>
                <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full">{info.items?.length}</span>
              </div>
              <div className="flex items-center gap-3"><PctBadge value={pct(info.total)} /><span className="font-semibold text-foreground w-32 text-right text-sm">{fmtSR(info.total)}</span></div>
            </button>
            {expanded[group] && info.items?.map((item, idx) => (
              <LineItem key={idx} label={item.name.replace(/_/g, ' ')} amount={item.total} pct={pct(item.total)} indent={3} color="text-muted-foreground" drillAccount={item.account} onDrill={drillAccount} />
            ))}
          </div>
        ))}

        {/* Net Profit */}
        <div className={`px-5 py-4 flex items-center justify-between border-t-2 ${data.net_profit >= 0 ? 'bg-success/10 border-success/30' : 'bg-error/10 border-error/30'}`}>
          <span className={`font-bold text-base ${data.net_profit >= 0 ? 'text-success' : 'text-error'}`}>Net Profit / (Loss)</span>
          <div className="flex items-center gap-3">
            <PctBadge value={data.net_margin} color={data.net_profit >= 0 ? 'bg-success/20 text-success' : 'bg-error/20 text-error'} />
            <span className={`font-bold text-lg w-32 text-right ${data.net_profit >= 0 ? 'text-success' : 'text-error'}`} data-testid="pnl-net-profit">{fmtSR(data.net_profit)}</span>
          </div>
        </div>
        <div className="px-5 pb-1 text-[10px] text-muted-foreground">Accounting net profit from the posted General Ledger (always excl. VAT).{data.margin_note ? ` Margin: ${data.margin_note}` : ''}</div>
        <details className="border-t border-border" data-testid="pnl-advanced">
          <summary className="px-5 py-3 cursor-pointer text-sm font-semibold text-primary select-none">
            Advanced: daily records vs GL (difference {fmtSR(data.reconciliation?.difference || 0)}), cash &amp; bank, supplier credit and repayments
          </summary>
          <div className="p-4 space-y-4">
            <PnlCompleteness data={data} />
            <PnlReconciliation rec={data.reconciliation} per={per} onDrill={drillAccount} />
            <PnlCashCreditSection sup={data.supplementary} per={per} />
          </div>
        </details>

        {/* Informational: supplier payments (AP movement, excluded from P&L) */}
        {data.accounts_payable_movement?.total > 0 && (
          <div className="border-t border-border bg-muted/30 px-5 py-3 text-xs">
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold text-muted-foreground uppercase tracking-wider text-[10px]">Supplier Payments (AP movement)</span>
              <span className="font-mono text-foreground/70">{fmtSR(data.accounts_payable_movement.total)}</span>
            </div>
            <p className="text-[10px] text-muted-foreground italic leading-relaxed">
              {data.accounts_payable_movement.note}
            </p>
            {(data.accounts_payable_movement.items || []).length > 0 && (
              <div className="mt-2 space-y-0.5">
                {data.accounts_payable_movement.items.map((it, i) => (
                  <div key={i} className="flex justify-between text-[11px] text-muted-foreground pl-2">
                    <span>{it.name}</span>
                    <span className="font-mono">{fmtSR(it.total)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Inter-Branch Position (injected from canonical inter-branch engine) */}
        {data.inter_branch && (data.inter_branch.breakdown?.length > 0) && (
          <div className="border-t border-border bg-indigo-50/60 px-5 py-3 text-xs" data-testid="pnl-inter-branch-footer">
            <div className="flex items-center justify-between mb-1.5">
              <span className="font-semibold text-indigo-700 uppercase tracking-wider text-[10px]">Inter-Branch Position</span>
              {data.inter_branch.scope === 'branch' ? (
                <span
                  className={`font-mono font-bold ${data.inter_branch.net_outstanding >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}
                  data-testid="pnl-inter-branch-net"
                >
                  {data.inter_branch.net_outstanding >= 0 ? 'Net owed to this branch ' : 'Net owed by this branch '}
                  {fmtSR(Math.abs(data.inter_branch.net_outstanding))}
                </span>
              ) : (
                <span className="font-mono font-bold text-indigo-700" data-testid="pnl-inter-branch-net-total">
                  Total outstanding {fmtSR(data.inter_branch.total_outstanding)}
                </span>
              )}
            </div>
            <div className="mt-1 space-y-0.5">
              {data.inter_branch.scope === 'branch'
                ? data.inter_branch.breakdown.map((r, i) => (
                    <div key={i} className="flex justify-between items-center text-[11px] pl-2 group/ib" data-testid={`pnl-inter-branch-row-${i}`}>
                      <span className="text-muted-foreground">
                        {r.direction === 'owes_us'
                          ? <><span className="font-medium text-foreground">{r.branch}</span> owes this branch</>
                          : <>This branch owes <span className="font-medium text-foreground">{r.branch}</span></>}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className={`font-mono ${r.direction === 'owes_us' ? 'text-emerald-600' : 'text-rose-600'}`}>{fmtSR(r.amount)}</span>
                        <button type="button" data-testid={`pnl-ib-settle-${i}`}
                          onClick={() => openSettle(
                            r.direction === 'we_owe'
                              ? { from_branch_id: data.inter_branch.branch_id, to_branch_id: r.branch_id, amount: r.amount, label: `This branch → ${r.branch}` }
                              : { from_branch_id: r.branch_id, to_branch_id: data.inter_branch.branch_id, amount: r.amount, label: `${r.branch} → this branch` }
                          )}
                          className="px-2 py-0.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-medium transition-colors">Settle</button>
                      </div>
                    </div>
                  ))
                : data.inter_branch.breakdown.map((p, i) => (
                    <div key={i} className="flex justify-between items-center text-[11px] pl-2" data-testid={`pnl-inter-branch-row-${i}`}>
                      <span className="text-muted-foreground">
                        <span className="font-medium text-foreground">{p.from_branch}</span> owes <span className="font-medium text-foreground">{p.to_branch}</span>
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-foreground/70">{fmtSR(p.amount)}</span>
                        <button type="button" data-testid={`pnl-ib-settle-${i}`}
                          onClick={() => openSettle({ from_branch_id: p.from_id, to_branch_id: p.to_id, amount: p.amount, label: `${p.from_branch} → ${p.to_branch}` })}
                          className="px-2 py-0.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-medium transition-colors">Settle</button>
                      </div>
                    </div>
                  ))}
            </div>
            <p className="text-[10px] text-muted-foreground italic mt-1.5 leading-relaxed">
              Cross-branch amounts one branch paid on behalf of another (after settlements). Not part of P&L — shown for cash reconciliation.
            </p>
          </div>
        )}

        {/* Inter-branch Settle dialog */}
        <Dialog open={!!settleTarget} onOpenChange={(o) => { if (!o) setSettleTarget(null); }}>
          <DialogContent className="max-w-sm" data-testid="ib-settle-dialog">
            <DialogHeader>
              <DialogTitle>Settle Inter-Branch Balance</DialogTitle>
              <DialogDescription>{settleTarget?.label} — records a payment + balanced journal entry.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-medium text-muted-foreground uppercase block mb-1">Amount</label>
                <Input type="number" value={settleAmount} onChange={(e) => setSettleAmount(e.target.value)} data-testid="ib-settle-amount" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-muted-foreground uppercase block mb-1">Payment mode</label>
                <Select value={settleMode} onValueChange={setSettleMode}>
                  <SelectTrigger data-testid="ib-settle-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="other">Other / Clearing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => setSettleTarget(null)}>Cancel</Button>
                <Button size="sm" onClick={doSettle} disabled={settling} data-testid="ib-settle-confirm">{settling ? 'Settling…' : 'Confirm Settlement'}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

      </CardContent></Card>

      {/* Closing Balance */}
      {showBalances && (
        <Card className="border-border bg-muted/50">
          <CardContent className="p-4 flex justify-between items-center">
            <span className="text-sm font-medium text-muted-foreground">Closing Balance (Opening + Net Profit)</span>
            <span className={`font-bold ${data.net_profit >= 0 ? 'text-success' : 'text-error'}`}>{fmtSR(data.net_profit)}</span>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function BranchView({ data, showBalances }) {
  const [expandedBranch, setExpandedBranch] = useState(null);
  const b = data?.branches || [];
  const g = data?.grand_total || {};

  return (
    <div id="pnl-content" className="space-y-5">
      {/* Grand Totals */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="border-success/30"><CardContent className="p-4"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Total Revenue</p><p className="text-xl font-bold text-success">{fmtSR(g.revenue)}</p></CardContent></Card>
        <Card className="border-error/30"><CardContent className="p-4"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Total Expenses</p><p className="text-xl font-bold text-error">{fmtSR(g.expenses)}</p></CardContent></Card>
        <Card className={`border-2 ${g.net_profit >= 0 ? 'border-success/30' : 'border-error/30'}`}><CardContent className="p-4"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Net Profit</p><p className={`text-xl font-bold ${g.net_profit >= 0 ? 'text-success' : 'text-error'}`}>{fmtSR(g.net_profit)}</p></CardContent></Card>
      </div>

      {/* Branch Comparison Table */}
      <Card><CardContent className="p-0">
        <div className="p-4 border-b bg-muted/50 flex items-center gap-2"><BarChart3 size={16} className="text-muted-foreground" /><h3 className="text-sm font-semibold text-foreground">Branch P&L Comparison</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="branch-pnl-table">
            <thead><tr className="border-b bg-muted/50">
              <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Branch</th>
              <th className="text-center p-3 text-xs font-semibold text-muted-foreground">Sales</th>
              <th className="text-right p-3 text-xs font-semibold text-muted-foreground">Revenue</th>
              <th className="text-right p-3 text-xs font-semibold text-muted-foreground">COGS</th>
              <th className="text-right p-3 text-xs font-semibold text-muted-foreground">Gross</th>
              <th className="text-right p-3 text-xs font-semibold text-muted-foreground">OpEx</th>
              <th className="text-right p-3 text-xs font-semibold text-muted-foreground">Net Profit</th>
              <th className="text-right p-3 text-xs font-semibold text-muted-foreground">Margin</th>
              <th className="w-10"></th>
            </tr></thead>
            <tbody>
              {b.map(br => (
                <tr key={br.branch_id} className={`border-b hover:bg-muted/50 cursor-pointer ${expandedBranch === br.branch_id ? 'bg-info/10' : ''}`} onClick={() => setExpandedBranch(expandedBranch === br.branch_id ? null : br.branch_id)}>
                  <td className="p-3 font-medium text-foreground">{br.branch_name}</td>
                  <td className="p-3 text-center text-muted-foreground">{br.sales_count}</td>
                  <td className="p-3 text-right text-success font-medium">{fmtSR(br.revenue)}</td>
                  <td className="p-3 text-right text-warning">{fmtSR(br.cost_of_sales)}</td>
                  <td className="p-3 text-right text-info">{fmtSR(br.gross_profit)}</td>
                  <td className="p-3 text-right text-error">{fmtSR(br.operating_expenses)}</td>
                  <td className={`p-3 text-right font-bold ${br.net_profit >= 0 ? 'text-success' : 'text-error'}`}>{fmtSR(br.net_profit)}</td>
                  <td className="p-3 text-right"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${br.net_margin >= 0 ? 'bg-success/20 text-success' : 'bg-error/20 text-error'}`}>{br.net_margin}%</span></td>
                  <td className="p-2">{expandedBranch === br.branch_id ? <ChevronDown size={14} /> : <ChevRight size={14} />}</td>
                </tr>
              ))}
            </tbody>
            {b.length > 0 && <tfoot><tr className="border-t-2 border-border bg-muted/50 font-bold">
              <td className="p-3">Total</td>
              <td className="p-3 text-center">{b.reduce((s,x) => s+x.sales_count,0)}</td>
              <td className="p-3 text-right text-success">{fmtSR(g.revenue)}</td>
              <td className="p-3 text-right text-warning">{fmtSR(b.reduce((s,x) => s+x.cost_of_sales,0))}</td>
              <td className="p-3 text-right text-info">{fmtSR(b.reduce((s,x) => s+x.gross_profit,0))}</td>
              <td className="p-3 text-right text-error">{fmtSR(b.reduce((s,x) => s+x.operating_expenses,0))}</td>
              <td className={`p-3 text-right ${g.net_profit >= 0 ? 'text-success' : 'text-error'}`}>{fmtSR(g.net_profit)}</td>
              <td className="p-3 text-right"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${g.net_profit >= 0 ? 'bg-success/20 text-success' : 'bg-error/20 text-error'}`}>{g.revenue ? (g.net_profit/g.revenue*100).toFixed(1) : 0}%</span></td>
              <td></td>
            </tr></tfoot>}
          </table>
        </div>
      </CardContent></Card>

      {/* Expanded Branch Detail */}
      {expandedBranch && (() => {
        const br = b.find(x => x.branch_id === expandedBranch);
        if (!br) return null;
        const pct = (v) => br.revenue ? ((v || 0) / br.revenue * 100).toFixed(1) : '0.0';
        return (
          <Card className="border-2 border-info/30 shadow-lg">
            <CardContent className="p-0">
              <div className="p-4 bg-info/10 border-b border-info/30 flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-foreground">{br.branch_name} — Detailed P&L</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{data.period?.start} to {data.period?.end} | {br.sales_count} sales</p>
                </div>
                <span className={`text-lg font-bold ${br.net_profit >= 0 ? 'text-success' : 'text-error'}`}>{fmtSR(br.net_profit)}</span>
              </div>

              {showBalances && <LineItem label="Opening Balance" amount={0} bold color="text-muted-foreground" bg="bg-muted/50" />}

              <SectionHeader label="Revenue" amount={br.revenue} pct="100" color="text-success" bgClass="bg-success/10" />
              <LineItem label="Sales" amount={br.revenue} pct="100" indent={1} />
              {br.online_sales > 0 && <LineItem label="(of which Online)" amount={br.online_sales} pct={pct(br.online_sales)} indent={2} color="text-purple-500" />}

              <SectionHeader label="Cost of Sales (Purchases)" amount={br.cost_of_sales} pct={pct(br.cost_of_sales)} color="text-warning" bgClass="bg-warning/10 border-t border-border" />
              <LineItem label="Supplier Purchases" amount={br.supplier_purchases} pct={pct(br.supplier_purchases)} indent={1} />

              <div className="bg-info/10 px-5 py-3 flex justify-between border-t border-border">
                <span className="font-bold text-info text-sm">Gross Profit</span>
                <div className="flex items-center gap-3"><PctBadge value={br.gross_margin} /><span className="font-bold text-info w-32 text-right">{fmtSR(br.gross_profit)}</span></div>
              </div>

              <SectionHeader label="Operating Expenses" amount={br.operating_expenses} pct={pct(br.operating_expenses)} color="text-error" bgClass="bg-error/10 border-t border-border" />
              {Object.entries(br.expense_categories || {}).filter(([k]) => !['Supplier', 'Supplier Purchase', 'supplier'].includes(k)).sort((a,b) => b[1]-a[1]).map(([cat, amt]) => (
                <LineItem key={cat} label={cat.replace(/_/g, ' ')} amount={amt} pct={pct(amt)} indent={1} color="text-muted-foreground" />
              ))}

              <div className={`px-5 py-3.5 flex justify-between border-t-2 ${br.net_profit >= 0 ? 'bg-success/10 border-success/30' : 'bg-error/10 border-error/30'}`}>
                <span className={`font-bold ${br.net_profit >= 0 ? 'text-success' : 'text-error'}`}>Net Profit / (Loss)</span>
                <div className="flex items-center gap-3"><PctBadge value={br.net_margin} color={br.net_profit >= 0 ? 'bg-success/20 text-success' : 'bg-error/20 text-error'} /><span className={`font-bold text-lg w-32 text-right ${br.net_profit >= 0 ? 'text-success' : 'text-error'}`}>{fmtSR(br.net_profit)}</span></div>
              </div>

              {showBalances && <LineItem label="Closing Balance" amount={br.net_profit} bold color={br.net_profit >= 0 ? 'text-success' : 'text-error'} bg="bg-muted/50" />}
            </CardContent>
          </Card>
        );
      })()}
    </div>
  );
}
